import PropTypes from 'prop-types';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Divider from '@zenbusiness/zui/Divider';
import Heading from '@zenbusiness/zui/Heading';
import Link from '@zenbusiness/zui/Link';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import VeloAvatar from '#client/components/VeloAssistantButton/VeloAvatar';
import * as ROUTES from '#client/constants/routes';
import { CONTACT_HOURS, VELO_DESCRIPTION } from '#client/utils/constants';

import * as Styled from './ContactMethods.style';

const veloAssistantClickstreamProps = {
  appSection: 'get-help',
  elementName: 'take_velo',
  elementText: 'Talk to Velo',
  dynamicProperties: {
    ai_project_name: 'velo_chat',
  },
};

const ContactMethods = ({ showIcons = true, showMethods = [] }) => {
  const { palette } = useTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { businessEntityUuid } = useBusinessEntity();

  const methods = [
    {
      icon: <VeloAvatar width={54} height={54} />,
      type: 'velo',
      title: 'Get Answers Fast with Velo',
      subtitle:
        "Velo uses AI plus ZenBusiness expertise to give you instant answers anytime. Still have questions? We'll connect you with someone on our customer support team to help you get the info you need.",
      description: VELO_DESCRIPTION,
      cta: (
        <Button
          variant="outlined"
          color="info"
          size="sm"
          clickstreamProps={veloAssistantClickstreamProps}
          href={
            businessEntityUuid
              ? `${ROUTES.VELO_GUIDE_COMPANY.replace('[businessEntityUuid]', businessEntityUuid)}/`
              : ROUTES.VELO_GUIDE
          }
        >
          Talk to Velo
        </Button>
      ),
    },
    {
      icon: <Styled.PhoneSupportIcon />,
      type: 'phone',
      title: 'Call Our Support Team',
      subtitle: 'Speak directly with a support specialist or our sales team to get real-time help.',
      name: 'Call during our business hours:',
      description: CONTACT_HOURS,
      cta: (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={0.5}>
          <Text weight="medium" size="sm">
            Customer Support:
          </Text>
          <Link href="tel:844-493-6249" weight="medium" size="sm">
            844-493-6249
          </Link>
          |
          <Text weight="medium" size="sm">
            Sales:
          </Text>
          <Link href="tel:888-514-2944" weight="medium" size="sm">
            888-514-2944
          </Link>
        </Box>
      ),
    },
  ];

  const showContactMethod = (type) =>
    showMethods.length === 0 || (showMethods?.length > 0 && showMethods.includes(type));

  const renderMethodContent = ({ cta, description, icon, name, title, type, subtitle }) => (
    <Box
      gap={{ xs: 2, md: 3 }}
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="center" color={palette.info.light} width={54} height={54}>
        {showIcons && icon}
      </Box>

      <Stack flex={1} gap={0.5}>
        <Heading size="md">{title}</Heading>
        <Text size="sm" color="secondary">
          {subtitle}
        </Text>
      </Stack>

      <Divider orientation={isMobile ? 'horizontal' : 'vertical'} />

      <Stack flex={1} gap={2}>
        <Text size="sm" data-testid={`contact-method-${type}`}>
          {name && <Text weight="medium">{name}</Text>}
          {description}
        </Text>
        <Box>{cta}</Box>
      </Stack>
    </Box>
  );

  return (
    <Stack spacing={1}>
      {methods
        .filter((method) => showContactMethod(method.type))
        .map((method) => (
          <Styled.MethodPaper variant="outlined" key={method.type}>
            {renderMethodContent(method)}
          </Styled.MethodPaper>
        ))}
    </Stack>
  );
};

ContactMethods.propTypes = {
  showIcons: PropTypes.bool,
  showMethods: PropTypes.arrayOf(PropTypes.string),
};

export default ContactMethods;
